import { useState } from 'react'

// Define the shape of our form data based on the image
export interface MedicalFormData {
  // Basic Information
  areYou: string
  location: string

  // Personal Details
  admissionDate: string
  reference: string
  regdNo: string
  name: string
  gender: 'M' | 'F' | 'Other' | ''
  age: string
  guardianName: string
  occupation: string
  address: string
  city: string
  pincode: string
  mobile: string
  chiefComplaints: string

  // Physical Examination
  thirst: string
  appetite: string
  sleep: string
  dreams: string
  foodHabits: string[]
  hygiene: string
  addiction: string
  lifestyle: string
  bodyType: string
  weight: string
  temperature: string
  mentalCondition: string[]
  mentalConditionOther: string
  bp: string
  bodyPalpation: string
  tongue: string
  pulse: string

  // Medical History
  medicalHistory: string[]

  // Objective Findings
  bowel: string
  bowelOther: string
  bowelAdditional: string
  urine: string
  urineFrequency: string
  urineFrequencyNight: string
  urineColor: string
  urineSmell: string
  skin: string
  skinAdditional: string
  scalp: string
  bodyHair: string
  nails: string
  bodyOdour: string
  mouthOdour: string
  edema: string
  eye: string
  ear: string
  mouth: string
  throat: string
  lips: string

  // Gynecology
  gynecologicalConditions: string[]
  isPregnant: string
  pregnancyMonths: string
  menopause: string
  mensurationDuration: string
  mensurationCycleDays: string
  mensurationPainful: string[]
  mensurationClots: string
  mensurationFlow: string

  // History
  pastIllness: string
  familyHistory: string
  labInvestigations: string
  diagnosis: string

  // Treatment
  evenDate: string
  oddDate: string
  reflexologyPointsText: string
  treatmentModalities: string[]

  // Attachments
  attachments: File[]

  // Consent
  consentAgreed: boolean
}

const initialData: MedicalFormData = {
  areYou: '',
  location: '',
  admissionDate: new Date().toISOString().split('T')[0],
  reference: '',
  regdNo: '',
  name: '',
  gender: '',
  age: '',
  guardianName: '',
  occupation: '',
  address: '',
  city: '',
  pincode: '',
  mobile: '',
  chiefComplaints: '',
  thirst: '',
  appetite: '',
  sleep: '',
  dreams: '',
  foodHabits: [],
  hygiene: '',
  addiction: '',
  lifestyle: '',
  bodyType: '',
  weight: '',
  temperature: '',
  mentalCondition: [],
  mentalConditionOther: '',
  bp: '',
  bodyPalpation: '',
  tongue: '',
  pulse: '',
  medicalHistory: [],
  bowel: '',
  bowelOther: '',
  bowelAdditional: '',
  urine: '',
  urineFrequency: '',
  urineFrequencyNight: '',
  urineColor: '',
  urineSmell: '',
  skin: '',
  skinAdditional: '',
  scalp: '',
  bodyHair: '',
  nails: '',
  bodyOdour: '',
  mouthOdour: '',
  edema: '',
  eye: '',
  ear: '',
  mouth: '',
  throat: '',
  lips: '',
  gynecologicalConditions: [],
  isPregnant: 'No',
  pregnancyMonths: '',
  menopause: 'No',
  mensurationDuration: '',
  mensurationCycleDays: '',
  mensurationPainful: [],
  mensurationClots: '',
  mensurationFlow: '',
  pastIllness: '',
  familyHistory: '',
  labInvestigations: '',
  diagnosis: '',
  evenDate: '',
  oddDate: '',
  reflexologyPointsText: '',
  treatmentModalities: [],
  attachments: [],
  consentAgreed: false,
}

export function useFormState() {
  const [formData, setFormData] = useState<MedicalFormData>(initialData)
  const [errors, setErrors] = useState<
    Partial<Record<keyof MedicalFormData, string>>
  >({})

  const handleChange = (field: keyof MedicalFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validate = () => {
    const newErrors: Partial<Record<keyof MedicalFormData, string>> = {}

    // Only validate Personal Details section fields
    if (!formData.name) newErrors.name = 'Patient name is required'
    if (!formData.mobile) newErrors.mobile = 'Contact number is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  return {
    formData,
    errors,
    handleChange,
    validate,
    setFormData,
  }
}
